// "Connect Cloudflare" (decision 0196): the owner pastes one scoped API token, the server sets up the tunnel over the
// owner's authenticated channel and wipes it. The browser never keeps the token (no storage, no URL, no log) and never
// calls the Cloudflare API; this module only decides what to show and builds the messages.
import type { ClientMessage, ServerMessage } from './protocol';

export type TunnelState = Extract<ServerMessage, { type: 'tunnel_state' }>;

/** What a setup token needs: the server's list (`tunnel_setup::PERMISSIONS`), shown as written text. */
export const PERMISSIONS = [
  'Account: Cloudflare Tunnel: Edit',
  'Zone: Zone: Read',
  'Zone: DNS: Edit',
  'Account: Workers Scripts: Edit',
  'Account: Cloudflare Calls: Edit',
] as const;

/** Cloudflare's own token page. A link that pre-selects the permissions is unverified (plan step 1.2), so the list above
 *  is shown beside a plain link, and nothing, least of all a token, rides in the address. */
export const TOKEN_PAGE = 'https://dash.cloudflare.com/profile/api-tokens';

/** How often an open card asks the server for `tunnel_state` again (the server answers only when asked). */
export const POLL_MS = 5000;
/** How often the app asks in the background so a stopped tunnel is noticed without a card open. */
export const WATCH_MS = 60_000;

export type TunnelView = 'unknown' | 'connect' | 'quick' | 'working' | 'stopped';

/** The card to show for a server's last answer. Only an owner tunnel can be "stopped": with none set up there is nothing to reconnect. */
export function viewOf(state: TunnelState | null | undefined): TunnelView {
  if (!state) return 'unknown';
  if (state.mode === 'owner') return state.status === 'stopped' ? 'stopped' : 'working';
  return state.mode === 'quick' ? 'quick' : 'connect';
}

/** The plain banner for a stopped owner tunnel, else null. */
export function stoppedNotice(state: TunnelState | null | undefined): string | null {
  return viewOf(state) === 'stopped' ? 'Cloudflare tunnel stopped.' : null;
}

export type TunnelAction = 'setup' | 'rotate' | 'disconnect';

/** The message for an action. The token leaves the caller here and is not copied anywhere else; an empty one is none. */
export function tunnelMessage(action: TunnelAction, token: string): ClientMessage {
  const t = token.trim();
  if (action === 'setup') return { type: 'tunnel_setup', token: t };
  if (action === 'rotate') return { type: 'tunnel_rotate', token: t };
  return t ? { type: 'tunnel_disconnect', token: t } : { type: 'tunnel_disconnect' };
}

/** "Created 2 Nov 2026"; empty when the server has no time. `created_at` is in seconds. */
export function createdText(createdAt: number | undefined): string {
  if (!createdAt) return '';
  return new Date(createdAt * 1000).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
